/**
 * useOrders.ts — fetch order history data
 *
 * Calls GET /api/broker/v1/orders (operator: all orders; tenant: own orders).
 *
 * WP-SL-V5 / DOPLA-688
 */

import { useEffect, useReducer, useCallback } from 'react';

export interface OrderRow {
  id: string;
  tenant_id: string | null;
  tenant_name: string | null;
  user_subject: string;
  rail: 'b2b_invoice' | 'b2c_card';
  status: 'pending_payment' | 'settled' | 'cancelled';
  total_cents: number;
  currency: string;
  settled_at: string | null;
  settled_by: string | null;
  settlement_ref: string | null;
  created_at: string;
  item_count: number;
}

export interface OrderItem {
  id: string;
  service_id: string;
  plan_id: string;
  subscription_id: string | null;
  price_cents: number;
  currency: string;
  created_at: string;
  service_name: string | null;
  plan_name: string | null;
  sub_status: string | null;
  approval_state: string | null;
}

export interface OrderDetail extends OrderRow {
  items: OrderItem[];
}

export type OrdersState =
  | { tag: 'idle' }
  | { tag: 'loading' }
  | { tag: 'loaded'; orders: OrderRow[] }
  | { tag: 'error'; errorCode?: string };

type Action =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS'; orders: OrderRow[] }
  | { type: 'FETCH_ERROR'; errorCode?: string };

function reducer(_: OrdersState, action: Action): OrdersState {
  switch (action.type) {
    case 'FETCH_START':   return { tag: 'loading' };
    case 'FETCH_SUCCESS': return { tag: 'loaded', orders: action.orders };
    case 'FETCH_ERROR':   return { tag: 'error', errorCode: action.errorCode };
    default:              return _;
  }
}

export function useOrders(
  token: string | null,
  filter?: { tenant_id?: string; status?: string },
) {
  const [state, dispatch] = useReducer(reducer, { tag: 'idle' });

  const load = useCallback(() => {
    if (!token) return;
    dispatch({ type: 'FETCH_START' });
    const params = new URLSearchParams();
    if (filter?.tenant_id) params.set('tenant_id', filter.tenant_id);
    if (filter?.status)    params.set('status', filter.status);
    const qs = params.toString() ? `?${params}` : '';
    fetch(`/api/broker/v1/orders${qs}`, {
      cache: 'no-store',
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (!res.ok) { dispatch({ type: 'FETCH_ERROR', errorCode: String(res.status) }); return; }
        return res.json() as Promise<{ orders: OrderRow[] }>;
      })
      .then((data) => {
        if (data) dispatch({ type: 'FETCH_SUCCESS', orders: data.orders });
      })
      .catch(() => dispatch({ type: 'FETCH_ERROR', errorCode: 'NETWORK_ERROR' }));
  }, [token, filter?.tenant_id, filter?.status]);

  useEffect(() => { load(); }, [load]);

  return { state, reload: load };
}
